/**
 * /benchmarks — Public risk benchmark page (Roadmap A).
 *
 * Anchored entirely to open public data (OFAC, NIST NVD, ENISA, FCA,
 * NY Fed, ISM…). Every datapoint links its original source. A
 * methodology footer makes the page defensible against the
 * "but how did you arrive at that number?" pushback that subjective
 * benchmarks invite. Until the Owner publishes
 * (`BENCHMARKS_PUBLISHED=true`), a yellow PREVIEW ribbon makes the
 * draft state unmistakable.
 */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ExternalLink, ShieldAlert, Database, ScaleIcon } from "lucide-react";
import axios from "axios";
import { PublicShell } from "@/components/PublicShell";
import { BenchmarkSparkline } from "@/components/BenchmarkSparkline";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function CadenceBadge({ cadence, auto }) {
  if (!cadence) return null;
  const palette = {
    weekly: "bg-emerald-100 text-emerald-700",
    monthly: "bg-emerald-100 text-emerald-700",
    annual: "bg-amber-100 text-amber-700",
    static: "bg-slate-200 text-slate-600",
  };
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${palette[cadence] || palette.static}`}
      title={auto ? "Auto-refreshed monthly from public feed" : "Manual refresh on this cadence"}
    >
      {cadence}{auto ? " · auto" : ""}
    </span>
  );
}

function DataCard({ dp }) {
  return (
    <li className="flex flex-col gap-1 border-b border-black/5 py-3 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-ek-ink/70">{dp.label}</span>
        <CadenceBadge cadence={dp.cadence} auto={!!dp.auto_refresh} />
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-2xl font-semibold text-ek-ink" data-testid="benchmark-value">
          {typeof dp.value === "number" ? dp.value.toLocaleString() : dp.value}
        </span>
        <span className="text-xs text-ek-ink/50">{dp.unit}</span>
        <span className="ml-auto"><BenchmarkSparkline keyId={dp.key} /></span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <a
          href={dp.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-ek-purple hover:underline"
        >
          Source: {dp.source} <ExternalLink size={10} />
        </a>
        {dp.last_refreshed_at && (
          <span className="text-[10px] text-ek-ink/40" title="Auto-fetched from public feed">
            refreshed {dp.last_refreshed_at.slice(0, 10)}
          </span>
        )}
      </div>
    </li>
  );
}

function CategoryCard({ cat }) {
  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm"
      data-testid={`benchmark-category-${cat.category_id}`}
    >
      <h2 className="font-display text-xl font-semibold text-ek-ink">{cat.title}</h2>
      <p className="mt-1 text-sm text-ek-ink/65">{cat.subtitle}</p>
      <ul className="mt-3">
        {(cat.datapoints || []).map((dp, i) => <DataCard dp={dp} key={i} />)}
      </ul>
      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-[12px] leading-relaxed text-ek-ink/60">
        <strong className="text-ek-ink/80">Method.</strong> {cat.method}
      </div>
    </section>
  );
}

export default function Benchmarks() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/benchmarks`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) {
    return (
      <PublicShell>
        <div className="mx-auto max-w-3xl px-6 py-16">
          <p className="text-rose-600">Failed to load benchmarks: {String(err)}</p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Helmet>
        <title>ERIO — Public risk benchmarks</title>
        <meta
          name="description"
          content="Public risk benchmarks anchored to open data — OFAC, NIST NVD, ENISA, FCA. Source-cited, date-stamped, defensible."
        />
      </Helmet>

      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="benchmarks-page">
        {data && !data.published && (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
            data-testid="benchmarks-preview-ribbon"
          >
            <ShieldAlert className="mt-0.5 shrink-0 text-amber-700" size={18} />
            <div className="text-sm text-amber-900">
              <strong>Preview — not for distribution.</strong> This page is awaiting Owner
              co-signature. The methodology footer below is the gate.
            </div>
          </div>
        )}

        <header className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ek-purple">
            ERIO Benchmarks
          </p>
          <h1 className="mt-2 font-display text-4xl font-semibold text-ek-ink sm:text-5xl">
            Public risk benchmarks
          </h1>
          <p className="mt-3 max-w-2xl text-base text-ek-ink/70">
            Anchored entirely to open public data — OFAC, EU Council, NIST NVD, ENISA, FCA, SEC,
            NY Fed, ISM. We don&apos;t aggregate or weight; we link each number to its source so you
            can verify it. No customer data is shown here.
          </p>
          {data && (
            <p className="mt-2 text-xs text-ek-ink/45" data-testid="benchmarks-as-of">
              Data as at <strong>{data.as_of}</strong>
            </p>
          )}
        </header>

        {!data && <p className="text-sm text-ek-ink/50">Loading…</p>}

        {data && (
          <div className="grid gap-6 md:grid-cols-2">
            {data.categories.map((cat) => <CategoryCard cat={cat} key={cat.category_id} />)}
          </div>
        )}

        {data && (
          <section
            className="mt-10 rounded-2xl border border-black/5 bg-slate-50/60 p-6"
            data-testid="benchmarks-methodology"
          >
            <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ek-ink">
              <ScaleIcon size={18} /> Methodology
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-ek-ink/75">
              {data.methodology.summary}
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-ek-ink/75">
              {data.methodology.data_principles.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Database className="mt-1 shrink-0 text-ek-purple" size={12} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-ek-ink/50">
              <strong>Refresh cadence:</strong> {data.methodology.review_cadence}
              {data.methodology.co_signed_by && (
                <> &middot; <strong>Co-signed by:</strong> {data.methodology.co_signed_by}</>
              )}
            </p>
          </section>
        )}
      </div>
    </PublicShell>
  );
}
